import React, { useCallback, useEffect, useState } from 'react';
import { App } from 'antd';

import StopItemTagModal from '../components/tags/StopItemTagModal';
import useItemTagSetting from './useItemTagSetting';
import { invalidateTagOwner } from './useCollectionTags';
import { turnOnItemTag } from '../services/itemTags';
import { isPendingIntake } from '../services/records';
import { subCollectionsOf } from '../utils/collectionFamily';
import { normalizeId } from '../utils/ids';
import { cleanupSummary, itemTagRefusal, itemTagsOn } from '../utils/itemTagSettings';
import { canManage as canManageCollection } from '../utils/permissions';

/**
 * USE AS A TAG / STOP USING AS A TAG for one item (docs/plans/tags.md §3.2,
 * §5.2 "Item ⋯ and read view (Chosen items)", ruled d35b, d39; build step
 * T4d). One definition for both doors — the item's ⋯ (useItemActions) and
 * its read view (ItemOwnTagRow) — so they never disagree.
 *
 * Offered only:
 * - on a saved item that is not awaiting review;
 * - to a manager of the ITEM's collection (ruled d35b), while it is not
 *   archived: a surface asks only where the person might manage —
 *   `canManage` given, or the collection in hand is managed and is the item's
 *   own or holds it as a sub-collection — and the API's answer settles it
 *   (useItemTagSetting's `canManage`: manage, and the collection not
 *   archived). A reader never makes the request and never sees either
 *   action. On an archived collection in hand nothing is asked either; an
 *   item there that IS a tag says so, and why it cannot change (`note`);
 * - on a card whose setting in the item's collection is **Chosen items**.
 *   Every item has no per-item exclusion in v1 (d39), and Off has none. When
 *   the collection in hand is the item's and none of its `cards[]` entries
 *   uses its items as tags, nothing is asked at all.
 *
 * **Use as a tag** turns the tag on at once (`PUT /api/items/{id}/own-tag`).
 * **Stop using as a tag…** opens StopItemTagModal. Either hands the item back
 * changed (`ownTagId` set or gone) through `onItemUpdated`, and holds the
 * change itself meanwhile, so a surface without that callback still reads it.
 *
 * → `{ action: { key, label, onClick } | null, used, note, modal, busy }`.
 */
export default function useItemOwnTag({
	item,
	card = null,
	title = '',
	cardType = 'Item',
	collection = null,
	canManage = false,
	enabled = true,
	onItemUpdated,
}) {
	const { message } = App.useApp();
	const itemId = normalizeId(item?._id);
	const saved = Boolean(itemId) && itemId !== '-1';
	const cardId = normalizeId(item?.card_id || card?._id);
	const collectionId = normalizeId(collection?._id);
	const itemCollectionId = normalizeId(item?.collection_id) || collectionId;
	// The collection in hand says "manager" for its own items and its
	// sub-collections' (grants flow down a family); a linked row from
	// another collection is not its to say.
	const holdsItem = Boolean(collectionId) && (itemCollectionId === collectionId
		|| subCollectionsOf(collection).some(sub => normalizeId(sub._id) === itemCollectionId));
	const mightManage = Boolean(canManage) || (holdsItem && canManageCollection(collection));
	// ⚑ The collection in hand IS the item's, and no card of it uses its items
	// as tags (`cards[]` carries the setting): nothing to ask. The common case
	// — a manager's list of a collection with no item tags — makes no request.
	const knownOff = itemCollectionId === collectionId && Array.isArray(collection?.cards)
		&& !collection.cards.some(ref => itemTagsOn(ref?.itemTags));
	// The collection in hand is archived, and so is the item's: a family
	// archives together, and every item-tag write refuses there.
	const archivedHere = holdsItem && collection?.status === 'archived';
	const hint = enabled && saved && Boolean(cardId) && !isPendingIntake(item)
		&& collection?.status !== 'archived' && mightManage && !knownOff;
	const setting = useItemTagSetting(itemCollectionId, cardId, { enabled: hint });
	// The API's `canManage` is false on an archived collection too (the item's
	// own may be archived when the one in hand is not).
	const offered = hint && setting.loaded && setting.canManage && setting.mode === 'chosen';

	// The item's own-tag stamp as this surface last changed it: a tag id,
	// null (turned off), or undefined — the item's own word.
	const [override, setOverride] = useState(undefined);
	useEffect(() => { setOverride(undefined); }, [itemId, item?.ownTagId]);
	const ownTagId = override !== undefined ? override : (normalizeId(item?.ownTagId) || null);
	const used = Boolean(ownTagId);
	// Why a manager sees no Stop using as a tag on an item that is one.
	const note = enabled && saved && used && archivedHere && mightManage
		? 'This item is used as a tag. The collection is archived, so that can’t change until it is unarchived.'
		: null;

	const [busy, setBusy] = useState(false);
	const [open, setOpen] = useState(false);
	const [mounted, setMounted] = useState(false);

	const useAsTag = useCallback(async () => {
		if (busy) return;
		setBusy(true);
		try {
			const tag = await turnOnItemTag(itemId);
			const tagId = normalizeId(tag?._id);
			setOverride(tagId || null);
			message.success(`“${title || 'This item'}” can now be put on other items as a tag.`);
			if (typeof onItemUpdated === 'function' && item) onItemUpdated({ ...item, ...(tagId ? { ownTagId: tagId } : {}) });
		} catch (error) {
			message.error(itemTagRefusal(error, 'The item could not be used as a tag. Please try again.'));
			// The card's setting moved under this surface: ask again.
			if (['item_tags_off', 'manage_required'].includes(error?.response?.data?.code)) setting.refresh();
		} finally {
			setBusy(false);
		}
	}, [busy, itemId, title, message, onItemUpdated, item, setting]);

	const openStop = useCallback(() => {
		setMounted(true);
		setOpen(true);
	}, []);

	const cleared = useCallback(() => {
		setOverride(null);
		if (typeof onItemUpdated === 'function' && item) {
			const next = { ...item };
			delete next.ownTagId;
			onItemUpdated(next);
		}
	}, [onItemUpdated, item]);

	const handleDone = useCallback((result, { outcome, name } = {}) => {
		cleared();
		const { done, failed } = cleanupSummary(result?.cleanup);
		if (outcome === 'convert' || result?.converted) {
			// A collection tag now: every vocabulary that lists the
			// collection's tags asks again.
			invalidateTagOwner(itemCollectionId);
			message.success(`“${result?.converted?.name || name || title}” is now a collection tag.`);
		} else {
			message.success([`“${title || 'This item'}” is no longer used as a tag.`, done].filter(Boolean).join(' '));
		}
		if (failed.length) message.warning(`${failed.length === 1 ? 'One reference' : `${failed.length} references`} could not be removed for you, and stop${failed.length === 1 ? 's' : ''} matching.`);
	}, [cleared, itemCollectionId, message, title]);

	const handleGone = useCallback(() => {
		cleared();
		message.info(`“${title || 'This item'}” was no longer used as a tag.`);
	}, [cleared, message, title]);

	const action = offered
		? (used
			? { key: 'stop-tag', label: 'Stop using as a tag…', onClick: openStop }
			: { key: 'use-as-tag', label: 'Use as a tag', onClick: useAsTag })
		: null;

	const modal = mounted ? (
		<StopItemTagModal
			open={open}
			onClose={() => setOpen(false)}
			item={item && ownTagId !== normalizeId(item.ownTagId) ? { ...item, ownTagId } : item}
			card={card}
			title={title}
			cardType={cardType}
			onDone={handleDone}
			onGone={handleGone}
		/>
	) : null;

	return { action, used, note, modal, busy, offered };
}
